/**
 * DocumentsV2.js — Unified document upload page for Business Single Application V2.
 *
 * Resolves document requirements based on V2 subtype:
 *  - type=1 (Plus): BUSINESS_V2_PLUS doc-type rules
 *  - type=2 (Prime): union of BUSINESS_V2_PLUS + BUSINESS_V2_PRIME rules
 *  - type=3 (Upgrade): BUSINESS_V2_UPGRADE doc-type rules
 *
 * This component is standalone and does NOT import from BusinessPeriodicReview/components/Documents.js.
 * The periodic review Documents component relies on /api/business/edd-info for doc requirements,
 * which is not applicable for V2 onboarding.
 *
 * V2 must resolve document requirements only from doc-type rules + doc-type metadata.
 * Do not call /api/application-doc/kyc-docs from this flow.
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import useDocTypeRules from '../../../../hooks/useDocTypeRules';
import { DialogContext } from '../../../../store/context/DialogContext';
import { DOC_TYPE_FORM_TYPES } from '../../../../util/docTypeRuleDefaults';
import { evaluateDocTypeRules } from '../../../../util/docTypeRuleEvaluator';
import { DOC_TYPE_SCOPE, splitDocTypesByScope, useDocTypes } from '../../../../util/docTypeRuleMeta';
import { formatIndividualName } from '../../../../util/docTypeRuleEvaluator';
import { doGET } from '../../../../util/HttpUtil';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import ProofOfDocumentCompactV2 from './ProofOfDocumentCompactV2';
import { V2_TYPE, getNextStepV2, WIZARD, WIZARD_NUM } from './helperV2';

/**
 * Resolve the doc-type form types to evaluate based on V2 subtype.
 */
const getDocTypeFormTypes = (v2Type) => {
    switch (v2Type) {
        case V2_TYPE.PLUS:
            return [DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS];
        case V2_TYPE.PRIME:
            // Type 2: union of Plus + Prime requirements
            return [DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS, DOC_TYPE_FORM_TYPES.BUSINESS_V2_PRIME];
        case V2_TYPE.UPGRADE:
            return [DOC_TYPE_FORM_TYPES.BUSINESS_V2_UPGRADE];
        default:
            return [DOC_TYPE_FORM_TYPES.BUSINESS_V2_PLUS];
    }
};

const isProofOfAddressDocMeta = (meta = {}) => {
    const normalized = String(meta?.type || meta?.field || meta?.showCategory || '')
        .toLowerCase()
        .replace(/[^a-z]/g, '');
    return normalized.includes('proofofaddress');
};

const getEffectiveValidateByAi = (meta = {}) => {
    if (!meta?.validateByAi) return false;
    // V2 onboarding should not live-verify proof-of-address documents.
    if (isProofOfAddressDocMeta(meta)) return false;
    return true;
};

const DocumentsV2 = ({
    loading,
    asLabel,
    setHasError,
    data,
    handleUpdate,
    handlePrev,
    editId,
    applicationForm,
    step,
    wizard,
    hideButtons,
    editMode,
    pageNum = WIZARD_NUM.Documents,
    v2Type = V2_TYPE.PLUS,
    jsonConfig,
}) => {
    const { showError } = useContext(DialogContext);
    const [internalLoading, setInternalLoading] = useState(false);
    const [errors, setErrors] = useState([]);

    // Live doc type metadata (label/error/file types) from /api/doc-type/grid
    const { docTypeMap, getMeta, loading: docTypesLoading, error: docTypesError } = useDocTypes();

    // Determine doc-type-rule form types to evaluate
    const docFormTypes = useMemo(() => getDocTypeFormTypes(v2Type), [v2Type]);

    // Build application data context for rule evaluation
    const ruleApplicationData = useMemo(
        () => ({ ...data, applicationForm, v2Type }),
        [applicationForm, data, v2Type],
    );

    // Load rules for the primary form type
    const {
        docTypes: primaryRuleDocTypes,
        loading: primaryDocRuleLoading,
        error: primaryDocRuleError,
        rules: primaryRules,
    } = useDocTypeRules({
        formType: docFormTypes[0],
        applicationData: ruleApplicationData,
    });

    // Load rules for secondary form type (Prime additional docs — only for type 2)
    const {
        docTypes: secondaryRuleDocTypes,
        loading: secondaryDocRuleLoading,
        error: secondaryDocRuleError,
        rules: secondaryRules,
    } = useDocTypeRules({
        formType: docFormTypes.length > 1 ? docFormTypes[1] : null,
        applicationData: ruleApplicationData,
        enabled: docFormTypes.length > 1,
    });

    // Merge doc types from all rule bundles
    const mergedDocTypes = useMemo(() => {
        const primary = primaryRuleDocTypes || [];
        const secondary = secondaryRuleDocTypes || [];
        return Array.from(new Set([...primary, ...secondary]));
    }, [primaryRuleDocTypes, secondaryRuleDocTypes]);

    const mergedRules = useMemo(() => {
        const primary = primaryRules || [];
        const secondary = secondaryRules || [];
        return [...primary, ...secondary];
    }, [primaryRules, secondaryRules]);

    // Split doc types by scope (business vs individual)
    const scopedDocTypes = useMemo(
        () => splitDocTypesByScope(mergedDocTypes, docTypeMap),
        [docTypeMap, mergedDocTypes],
    );

    const businessDocTypesToRender = useMemo(
        () => Array.from(new Set(scopedDocTypes.business || [])),
        [scopedDocTypes.business],
    );

    const businessDocMetas = useMemo(() => {
        return businessDocTypesToRender.map((docType) =>
            getMeta(docType, { scope: DOC_TYPE_SCOPE.BUSINESS })
        );
    }, [businessDocTypesToRender, getMeta]);

    // Per-individual doc requirements
    const individualDocRequirements = useMemo(() => {
        const individuals = Array.isArray(data?.individuals) ? data.individuals : [];
        const individualDocTypes = scopedDocTypes.individual || [];

        if (!individualDocTypes.length || !individuals.length) return [];

        return individuals
            .map((individual) => {
                // Evaluate rules per-individual
                const matchedDocTypes = evaluateDocTypeRules(mergedRules, {
                    ...data,
                    individuals: [individual],
                }).filter((docType) => getMeta(docType).scope === DOC_TYPE_SCOPE.INDIVIDUAL);

                if (!matchedDocTypes.length) return null;

                return {
                    uboId: individual?._id,
                    individual,
                    displayName: formatIndividualName(individual),
                    docTypes: matchedDocTypes,
                };
            })
            .filter(Boolean);
    }, [data, scopedDocTypes.individual, mergedRules, getMeta]);

    // Error display
    useEffect(() => {
        if (primaryDocRuleError) {
            showError(primaryDocRuleError?.message || "Failed to load document rules.");
        }
    }, [primaryDocRuleError, showError]);

    useEffect(() => {
        if (secondaryDocRuleError) {
            showError(secondaryDocRuleError?.message || "Failed to load secondary document rules.");
        }
    }, [secondaryDocRuleError, showError]);

    useEffect(() => {
        if (docTypesError) {
            showError(docTypesError?.message || "Failed to load document types.");
        }
    }, [docTypesError, showError]);

    // Document upload check
    const checkIsDocumentUploaded = async ({ recordId, ubo_id, type }) => {
        try {
            const response = await doGET(
                `/api/application-doc/grid?rows=-1&form_id=${recordId}${ubo_id ? `&ubo_id=${ubo_id}` : ''}&type=${type}`,
            );
            if (response?.status === 200 && response?.data?.rows?.[0]?.attachment?.urls?.length) {
                return true;
            }
        } catch (error) {
            showError(error);
        }
        return false;
    };

    const validateBusinessDocuments = async () => {
        if (!businessDocMetas?.length) {
            return { isValid: true, busErrors: [] };
        }

        const collectedErrors = [];
        let isValid = true;

        for (const meta of businessDocMetas) {
            const isUploaded = await checkIsDocumentUploaded({ recordId: editId, type: meta.type });
            if (!isUploaded) {
                isValid = false;
                collectedErrors.push({ type: meta.type, error: meta.errorMessage });
            }
        }

        return { isValid, busErrors: collectedErrors };
    };

    const handleSubmit = async () => {
        setInternalLoading(true);
        setErrors([]);

        const aggregatedErrors = [];

        // Validate individual documents
        for (const entry of individualDocRequirements) {
            const { uboId, docTypes, individual, displayName } = entry;
            if (!docTypes?.length) continue;

            for (const docType of docTypes) {
                const meta = getMeta(docType, { scope: DOC_TYPE_SCOPE.INDIVIDUAL, individual, displayName });
                const isUploaded = await checkIsDocumentUploaded({ ubo_id: uboId, recordId: editId, type: meta.type });
                if (!isUploaded) {
                    aggregatedErrors.push({
                        error: meta.errorMessage,
                        type: docType,
                        ubo_id: uboId,
                    });
                }
            }
        }

        // Validate business documents
        const { isValid: isBusinessValid, busErrors } = await validateBusinessDocuments();
        const combinedErrors = [...aggregatedErrors, ...busErrors];

        if (combinedErrors.length > 0 || !isBusinessValid) {
            setHasError(true);
            setErrors(combinedErrors);
            if (combinedErrors[0]?.error) {
                showError(combinedErrors[0]?.error);
            }
            setInternalLoading(false);
            return;
        }

        setHasError(false);
        const nextStep = getNextStepV2(wizard, v2Type, data, jsonConfig);
        const isFinalDocumentStep = nextStep?.wizard === WIZARD.ThankYou;
        await handleUpdate({
            nextStep,
            isLastStep: isFinalDocumentStep,
            isfetchDetail: isFinalDocumentStep,
            skipUpdate: true,
        });
        setInternalLoading(false);
    };

    const noDocumentsRequired = !businessDocMetas?.length && !individualDocRequirements?.length;
    const isDocumentPageReady = !primaryDocRuleLoading && !secondaryDocRuleLoading && !docTypesLoading;

    const renderBusinessDocument = (meta) => {
        return (
            <ProofOfDocumentCompactV2
                key={meta.type}
                dataTestId={meta.dataTestId}
                recordId={editId}
                asLabel={asLabel}
                label={meta.label}
                field={meta.field}
                showCategory={meta.showCategory}
                type={meta.type}
                single={meta.single}
                supportedFiles={meta.supportedFiles}
                validateByAi={false}
                error={errors.find((err) => err.type === meta.type)?.error}
            />
        );
    };

    const renderUboDocument = (entry, docType) => {
        const meta = getMeta(docType, {
            scope: DOC_TYPE_SCOPE.INDIVIDUAL,
            individual: entry.individual,
            displayName: entry.displayName,
        });

        return (
            <ProofOfDocumentCompactV2
                key={`${entry.uboId}-${docType}`}
                dataTestId={`${meta.dataTestId}-${entry.uboId || 'ubo'}`}
                recordId={editId}
                asLabel={asLabel}
                label={meta.label}
                field={meta.field}
                showCategory={meta.showCategory}
                type={meta.type}
                ubo_id={entry.uboId}
                single={meta.single}
                supportedFiles={meta.supportedFiles}
                validateByAi={false}
                error={errors.find((err) => err.ubo_id === entry.uboId && err.type === docType)?.error}
            />
        );
    };

    return (
        <div data-testid='business-v2-documents-step'>
            <h2 className='pt-3'>Document Upload Center</h2>
            <p style={{ fontSize: 12, fontStyle: 'italic' }}>
                Please upload all of the required documents for your application. If you do not have them ready, you can return later to
                complete the application.
            </p>

            {!isDocumentPageReady ? (
                <div style={{ width: '100%', height: 'calc(100vh - 340px)' }} className='d-flex justify-content-center align-items-center'>
                    <span className='text-muted'>Fetching document requirements…</span>
                </div>
            ) : (
                <>
                    <div className='py-2'>Important things to note:</div>
                    <ul>
                        <li>All documents must be in English or accompanied by an official certified translated copy.</li>
                        <li>Register of directors and shareholders must be certified by CPA/Lawyer/Notary.</li>
                        <li>Please read the help text below the upload box for each document and its requirements.</li>
                    </ul>

                    {noDocumentsRequired && (
                        <Card className='p-3 mb-3'>
                            <div className='fw-semibold'>No documents required at this stage.</div>
                            <div className='text-muted small'>We will notify you if additional verification materials are required later.</div>
                        </Card>
                    )}

                    {individualDocRequirements.map((entry, idx) => (
                        <div key={entry.uboId || idx}>
                            {entry.docTypes.map((docType) => renderUboDocument(entry, docType))}
                        </div>
                    ))}

                    {businessDocMetas.map((meta) => renderBusinessDocument(meta))}

                    <div className='mt-4'>
                        <ButtonGroupNew
                            onNextClick={handleSubmit}
                            onBackClick={handlePrev}
                            showPageNum={false}
                            pageNum={pageNum}
                            step={pageNum}
                            isDisabled={asLabel}
                            isReadyForReviewAction={isDocumentPageReady}
                            reviewPendingReason="Please wait while document requirements finish loading."
                            asLabel={asLabel}
                            loading={internalLoading || loading || !isDocumentPageReady}
                            hideSubmitButton={hideButtons}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default DocumentsV2;
